// THE SETUP CONVERSATION — the new-account welcome's assistant path, drawn on
// the setup surface (components/OnboardingWizard.js shows it at the
// 'assistant' step; functions/onboarding.js onboardingChat* drive it). The
// same choices as the wizard's click steps (one plan, `onboardingPlan`), asked
// one at a time; every answer applies at once.
//
// Built from the D2 kit: the tonal capsule answers (AppCapsuleBtnToolbar), the
// Personalise panel's own sky tile (HomeSkyTile), the kit's input group for
// the ask field. Surfaces differ by fill, never by a border.

// ── The conversation ────────────────────────────────────────────────────────

// THE SHEET'S BUDGET: the answers and the ask field keep their size and the
// thread is the part that gives way — on a short window the sky grid pushed
// the ask field below the sheet's fold (live on dev 57290d93f at 757px).
export const OnboardingChat = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'A',
  flex: '1 1 auto',
  minWidth: '0',
  minHeight: '0',

  // The lines so far. A reversed column keeps the newest line in view with no
  // scrolling code (the fly rail's scroller). It is as tall as its lines, up
  // to a cap, then scrolls — a fixed block size left an empty band above the
  // first two lines (live on dev 76305a52b) — and it shrinks before anything
  // else in the sheet does, down to two lines.
  OnboardingThread: {
    role: 'log',
    ariaLive: 'polite',
    ariaLabel: (el) => el.call('polyglot', 'workspace.onboarding.chat.title'),
    display: 'flex',
    flexDirection: 'column-reverse',
    flex: '0 1 auto',
    maxHeight: '40dvh',
    minHeight: 'D',
    minWidth: '0',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    ThreadLines: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'Z1',
      minWidth: '0',
      childExtends: 'OnboardingTurn',
      childrenAs: 'state',
      childrenDeps: [(el, s) => s.root && s.root._onboardingFlow],
      children: (el, s) => {
        const flow = s.root && s.root._onboardingFlow
        return ((flow && flow.turns) || []).map((t) => ({ ...t, key: t.id }))
      }
    }
  },

  // What Your work's answer sets up — the apply's own lines, while it runs and
  // after (components/OnboardingStackStep.js).
  OnboardingSetupLines: { flexShrink: '0' },

  // The answers to the question being asked: chips, described options or the
  // skies, then the question's own skip.
  OnboardingAnswers: {
    hide: (el, s) => !el.call('onboardingQuestion', s.root),
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    flexShrink: '0',
    minWidth: '0',
    AnswerChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'Y',
      minWidth: '0',
      childExtends: 'OnboardingChoice',
      childrenAs: 'state',
      children: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        if (!q || q.kind) return []
        return (q.choices || []).map((c) => ({ ...c, key: q.id + '-' + c.value }))
      }
    },
    AnswerOptions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F)), 1fr))',
      gap: 'Y',
      minWidth: '0',
      childExtends: 'OnboardingOption',
      childrenAs: 'state',
      children: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        if (!q || q.kind !== 'option') return []
        return (q.choices || []).map((c) => ({ ...c, key: q.id + '-' + c.value }))
      }
    },
    AnswerSkies: {
      role: 'group',
      ariaLabel: (el) => el.call('polyglot', 'workspace.personalize.background'),
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 'Y',
      minWidth: '0',
      childExtends: 'OnboardingSkyChoice',
      childrenAs: 'state',
      children: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        if (!q || q.kind !== 'sky') return []
        return (q.choices || []).map((c) => ({ ...c, key: 'sky-' + c.value }))
      }
    },
    AppCapsuleBtnGhost: {
      compact: true,
      alignSelf: 'flex-start',
      hide: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        return !q || !q.skip
      },
      text: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        return el.call('polyglot', (q && q.skip) || 'workspace.onboarding.chat.skipQuestion')
      },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingChatSkip')
      }
    }
  },

  OnboardingAsk: {}
}

// One line of the conversation: the assistant's (its glyph, plain text) or the
// person's answer (a sent bubble on the `selected` plate with `title` ink — the
// quiet monochrome of the design direction; a raw hue is refused by the design
// language lock, rule raw-hue-ink). A line never changes sides, so the side is
// a create-time case.
export const OnboardingTurn = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'Z',
  minWidth: '0',
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationFillMode: 'backwards',
  isMine: (el, s) => s.from === 'me',
  '.isMine': { justifyContent: 'flex-end' },

  TurnGlyph: {
    hide: (el, s) => s.from === 'me',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'avatarXs',
    round: 'radiusPill',
    theme: 'layer',
    color: 'title',
    ariaHidden: 'true',
    Icon: { name: 'magicstar', boxSize: 'iconXs' }
  },

  TurnWords: {
    tag: 'p',
    margin: '0',
    minWidth: '0',
    maxWidth: '100%',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'title',
    overflowWrap: 'break-word',
    lang: (el, s) => s.lang || null,
    text: (el, s) => el.call('onboardingText', s.line),
    isMine: (el, s) => s.from === 'me',
    '.isMine': {
      maxWidth: '80%',
      padding: 'Y2 Z2',
      round: 'B B Y B',
      background: 'selected',
      color: 'title'
    }
  }
}

// A short answer — the tonal capsule (Rule 65: its states are the kit's).
export const OnboardingChoice = {
  extends: 'AppCapsuleBtnToolbar',
  gap: 'Y',
  onRender: (el) => el.call('onboardingFocusAnswer', el),
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('onboardingChatAnswer', s.value)
  },
  Icon: {
    if: (el, s) => !!s.icon,
    name: (el, s) => s.icon || 'star',
    boxSize: 'iconSm'
  },
  ChoiceLabel: {
    tag: 'span',
    lang: (el, s) => s.lang || null,
    text: (el, s) => el.call('polyglot', s.label || '')
  }
}

// A described answer — a tonal row with its name and one line about it.
export const OnboardingOption = {
  tag: 'button',
  type: 'button',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'W',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'Z A',
  border: 'none',
  round: 'radiusCard',
  theme: 'layer',
  color: 'title',
  textAlign: 'start',
  cursor: 'pointer',
  transition: 'background-color A defaultBezier, transform Z defaultBezier',
  ':hover': { background: 'selected' },
  ':active': { background: 'selected', transform: 'scale(0.99)' },
  onRender: (el) => el.call('onboardingFocusAnswer', el),
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('onboardingChatAnswer', s.value)
  },
  OptionName: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '500',
    lineHeight: '1.4',
    overflowWrap: 'break-word',
    text: (el, s) => el.call('polyglot', s.label || '')
  },
  OptionHint: {
    tag: 'span',
    fontSize: 'Z',
    lineHeight: '1.45',
    color: 'caption',
    overflowWrap: 'break-word',
    text: (el, s) => el.call('polyglot', s.hint || '')
  }
}

// A sky — the Personalise panel's own tile (its art, its pressed state); the
// pick answers the question instead of writing the look directly.
export const OnboardingSkyChoice = {
  extends: 'HomeSkyTile',
  onRender: (el) => el.call('onboardingFocusAnswer', el),
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('onboardingChatAnswer', s.value)
  }
}

// Ask anything — the question goes to the real assistant (the composer's own
// path, functions/onboarding.js onboardingChatHandoff) and the setup closes.
// While Your idea is asked, the field takes the idea instead: a typed
// sentence is the answer (onboardingChatTyped).
export const OnboardingAsk = {
  tag: 'form',
  novalidate: true,
  state: { draft: '' },
  display: 'flex',
  alignItems: 'center',
  gap: 'Y',
  flexShrink: '0',
  minWidth: '0',
  onSubmit: (e, el, s) => {
    e.preventDefault()
    const text = String(s.draft || '').trim()
    if (!text) return
    s.update({ draft: '' })
    el.call('onboardingChatTyped', text)
  },
  AppInputGroup: {
    flex: '1 1 auto',
    AppInputBare: {
      type: 'text',
      name: 'onboardingAsk',
      autocomplete: 'off',
      enterkeyhint: 'send',
      ariaLabelDeps: [(el, s) => s.root && s.root._onboardingFlow],
      ariaLabel: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        return el.call(
          'polyglot',
          q && q.typed ? 'workspace.onboarding.idea.label' : 'workspace.onboarding.chat.askLabel'
        )
      },
      placeholderDeps: [(el, s) => s.root && s.root._onboardingFlow],
      placeholder: (el, s) => {
        const q = el.call('onboardingQuestion', s.root)
        return el.call(
          'onboardingText',
          q && q.typed
            ? 'workspace.onboarding.idea.placeholder'
            : 'workspace.onboarding.chat.askPlaceholder'
        )
      },
      value: (el, s) => s.draft || '',
      onInput: (e, el, s) => s.update({ draft: String((e.target && e.target.value) || '') })
    }
  },
  CompactIconButton: {
    type: 'submit',
    theme: 'primary',
    icon: 'arrowRight',
    disabled: (el, s) => !String(s.draft || '').trim() || null,
    ariaLabel: (el) => el.call('polyglot', 'workspace.onboarding.chat.askSend'),
    title: (el) => el.call('polyglot', 'workspace.onboarding.chat.askSend')
  }
}
